import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Formik, Form, Field } from 'formik'
import * as Yup from 'yup'
import { toast } from 'react-toastify'
import {
  FaEnvelope, FaLock, FaEye, FaEyeSlash,
  FaTruck, FaBuilding, FaExclamationCircle,
} from 'react-icons/fa'
import { registerUser, clearRegistrationSuccess } from 'store/slices/authSlice'
import '../../assets/scss/pages/Auth.scss'

const RegisterSchema = Yup.object().shape({
  email: Yup.string().email('Invalid email address').required('Email is required'),
  password: Yup.string()
    .min(8, 'At least 8 characters')
    .matches(/[a-z]/, 'At least one lowercase letter')
    .matches(/[A-Z]/, 'At least one uppercase letter')
    .matches(/[0-9]/, 'At least one number')
    .matches(/[@$!%*?&#]/, 'At least one special character (@$!%*?&#)')
    .required('Password is required'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password'), null], 'Passwords do not match')
    .required('Please confirm your password'),
  companyType: Yup.string()
    .oneOf(['TRANSPORTER', 'TRUCK_OWNER', 'BOTH'], 'Invalid company type')
    .required('Company type is required'),
})

const Register = () => {
  const navigate  = useNavigate()
  const dispatch  = useDispatch()
  const { loading, error, registrationSuccess } = useSelector(state => state.auth)
  const [showPassword,        setShowPassword]        = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  if (registrationSuccess) {
    toast.success('Account created! Please sign in.')
    dispatch(clearRegistrationSuccess())
    navigate('/login')
  }

  const handleSubmit = values => {
    const { confirmPassword, ...userData } = values
    dispatch(registerUser(userData))
  }

  return (
    <div className="lg-wrap">

      {/* ── LEFT BRAND PANEL ── */}
      <div className="lg-panel">
        <div className="lg-panel-top">
          <div className="lg-logo">
            <div className="lg-logo-icon"><FaTruck /></div>
            <div className="lg-logo-name">TradeSetu <span>Transport</span></div>
          </div>

          <h1 className="lg-headline">
            Join the<br /><em>smarter</em><br />logistics network.
          </h1>
          <p className="lg-sub">
            Create your company account and start managing
            freight, trucks, drivers and bilties — all from one dashboard.
          </p>

          <div className="lg-features">
            {[
              'Freight Requests & Proposals',
              'Truck & Driver Management',
              'Digital Bilty & Compliance',
            ].map(f => (
              <div className="lg-feature" key={f}>
                <div className="lg-feature-dot">✓</div>
                <span className="lg-feature-text">{f}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="lg-panel-bottom">
          © {new Date().getFullYear()} TradeSetu · Transport Management Platform
        </div>
      </div>

      {/* ── RIGHT FORM PANEL ── */}
      <div className="lg-form-wrap">
        <div className="lg-card">

          {/* mobile logo */}
          <div className="lg-mobile-logo">
            <div className="lg-mobile-logo-icon"><FaTruck /></div>
            <div className="lg-mobile-logo-name">TradeSetu</div>
          </div>

          <div className="lg-card-top">
            <h2 className="lg-card-title">Create account</h2>
            <p className="lg-card-sub">Get started — it only takes a minute</p>
          </div>

          {error && (
            <div className="lg-error">
              <FaExclamationCircle className="lg-error-icon" />
              {error}
            </div>
          )}

          <Formik
            initialValues={{ email: '', password: '', confirmPassword: '', companyType: 'TRANSPORTER' }}
            validationSchema={RegisterSchema}
            onSubmit={handleSubmit}
          >
            {({ errors, touched, isValid, dirty }) => (
              <Form className="lg-form">

                {/* Email */}
                <div className="lg-field">
                  <label className="lg-label"><FaEnvelope /> Email Address</label>
                  <div className="lg-input-wrap">
                    <FaEnvelope className="lg-input-icon" />
                    <Field
                      type="email"
                      name="email"
                      className={`lg-input ${errors.email && touched.email ? 'lg-input--error' : ''}`}
                      placeholder="you@company.com"
                      autoComplete="email"
                    />
                  </div>
                  {errors.email && touched.email && <span className="lg-field-error">{errors.email}</span>}
                </div>

                {/* Register As */}
                <div className="lg-field">
                  <label className="lg-label"><FaBuilding /> Register As</label>
                  <div className="lg-input-wrap">
                    <FaBuilding className="lg-input-icon" />
                    <Field
                      as="select"
                      name="companyType"
                      className={`lg-input lg-select ${errors.companyType && touched.companyType ? 'lg-input--error' : ''}`}
                    >
                      <option value="TRANSPORTER">Transporter — Manage freight &amp; transport requests</option>
                      <option value="TRUCK_OWNER">Truck Owner — List trucks &amp; accept nominations</option>
                      <option value="BOTH">Both — Full platform access</option>
                    </Field>
                  </div>
                  {errors.companyType && touched.companyType && <span className="lg-field-error">{errors.companyType}</span>}
                </div>

                {/* Password */}
                <div className="lg-field">
                  <label className="lg-label"><FaLock /> Password</label>
                  <div className="lg-input-wrap">
                    <FaLock className="lg-input-icon" />
                    <Field
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      className={`lg-input ${errors.password && touched.password ? 'lg-input--error' : ''}`}
                      placeholder="Create a strong password"
                      autoComplete="new-password"
                    />
                    <button type="button" className="lg-pw-toggle" onClick={() => setShowPassword(p => !p)} tabIndex={-1}>
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                  {errors.password && touched.password && <span className="lg-field-error">{errors.password}</span>}
                  {!errors.password && touched.password && (
                    <span className="lg-field-hint">✓ Strong password</span>
                  )}
                </div>

                {/* Confirm Password */}
                <div className="lg-field">
                  <label className="lg-label"><FaLock /> Confirm Password</label>
                  <div className="lg-input-wrap">
                    <FaLock className="lg-input-icon" />
                    <Field
                      type={showConfirmPassword ? 'text' : 'password'}
                      name="confirmPassword"
                      className={`lg-input ${errors.confirmPassword && touched.confirmPassword ? 'lg-input--error' : ''}`}
                      placeholder="Re-enter your password"
                      autoComplete="new-password"
                    />
                    <button type="button" className="lg-pw-toggle" onClick={() => setShowConfirmPassword(p => !p)} tabIndex={-1}>
                      {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                  {errors.confirmPassword && touched.confirmPassword && <span className="lg-field-error">{errors.confirmPassword}</span>}
                </div>

                {/* Submit */}
                <button type="submit" className="lg-btn" disabled={loading || !isValid || !dirty}>
                  {loading
                    ? <><span className="spinner-border spinner-border-sm" />Creating account…</>
                    : 'Create Account'}
                </button>

                <div className="lg-footer">
                  Already have an account?{' '}
                  <Link to="/login">Sign in</Link>
                </div>

              </Form>
            )}
          </Formik>
        </div>
      </div>

    </div>
  )
}

export default Register
